import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import HeadingText from '@ui/atoms/HeadingText';
import SubheadingText from '@ui/atoms/SubheadingText';
import TextButton from '@ui/atoms/TextButton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import spaced from '@ui/styled/Spaced';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import DriversLicense from '@assets/vectors/drivers-license.inline.svg';
import Lock from '@assets/vectors/lock-icon.inline.svg';
import SocialSecurityNumber from '@assets/vectors/social-security-number.inline.svg';
import TakingSelfie from '@assets/vectors/taking-selfie.inline.svg';

import theme from '@theme/FolxTheme';

export interface IIdInfoStepContent {
	title: string;
	description: string;
	document: { title: string; description: string };
	selfie: { title: string; description: string };
	socialSecurityNumber: { title: string; description: string };
	securityDisclaimer: { title: string; description: string };
}

export interface IMemberIdInfoStepProps extends IFlowStepProps {
	pageContent: IIdInfoStepContent;
	showSocialSecurityCopy?: boolean;
}

const SpacedColumn = spaced(FlexColumn);

const StyledTopic = styled(FlexRow)`
	gap: ${theme.spacing.sm};
	svg {
		flex: 0 0 auto;
		width: ${theme.spacing.smmd};
	}
`;

const MemberIdInfoStep: FC<IMemberIdInfoStepProps> = ({
	stepKey,
	pageContent,
	showSocialSecurityCopy = false,
}) => {
	const context = useContext(FlowContext);
	const { handleNavNext } = context;
	const handleNavigate = () => handleNavNext(stepKey);
	const { title, description, document, selfie, socialSecurityNumber, securityDisclaimer } =
		pageContent;
	return (
		<CenteredGridItem>
			<FlexColumn>
				<HeadingText>{title}</HeadingText>

				<BasicText>{description}</BasicText>
			</FlexColumn>

			<SpacedColumn gap="sm" marginTop="md" marginBottom="xl">
				<StyledTopic>
					<DriversLicense />
					<FlexColumn gap="sm">
						<SubheadingText>{document.title}</SubheadingText>
						<BasicText>{document.description}</BasicText>
					</FlexColumn>
				</StyledTopic>

				<StyledTopic>
					<TakingSelfie />
					<FlexColumn gap="sm">
						<SubheadingText>{selfie.title}</SubheadingText>
						<BasicText>{selfie.description}</BasicText>
					</FlexColumn>
				</StyledTopic>

				{showSocialSecurityCopy && (
					<StyledTopic>
						<SocialSecurityNumber />
						<FlexColumn gap="sm">
							<SubheadingText>{socialSecurityNumber.title}</SubheadingText>
							<BasicText>{socialSecurityNumber.description}</BasicText>
						</FlexColumn>
					</StyledTopic>
				)}

				<StyledTopic>
					<Lock />
					<FlexColumn gap="sm">
						<SubheadingText>{securityDisclaimer.title}</SubheadingText>
						<BasicText>{securityDisclaimer.description}</BasicText>
						<TextButton
							variant="underline"
							href="https://www.folxhealth.com/legal/security"
							newTab
							label="Learn more"
						/>
					</FlexColumn>
				</StyledTopic>
			</SpacedColumn>

			<Button label="Continue" labelSize="md" variant="secondary" onClick={handleNavigate} />
		</CenteredGridItem>
	);
};

export default MemberIdInfoStep;
